import * as React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Drawer,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
  ButtonBase
} from '@mui/material';
import { drawerClasses } from '@mui/material/Drawer';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';

function SidePanel({
  open,
  onClose,
  children,
  title,
  closeOnOutsideClick = true,
  width = 480,
  minWidth = 360,
  maxWidth = 900,
}) {
  const { t } = useTranslation('main');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [panelWidth, setPanelWidth] = React.useState(width);
  const isResizing = React.useRef(false);
  const frameId = React.useRef(null);

  const handleClose = (_, reason) => {
    if (!closeOnOutsideClick && reason === 'backdropClick') return;
    onClose();
  };

  React.useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing.current) return;

      const newWidth = window.innerWidth - e.clientX;
      if (frameId.current) cancelAnimationFrame(frameId.current);
      frameId.current = requestAnimationFrame(() => {
        setPanelWidth(Math.max(minWidth, Math.min(newWidth, maxWidth)));
      });

    };

    const stopResizing = () => {
      isResizing.current = false;
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', stopResizing);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', stopResizing);
      if (frameId.current) cancelAnimationFrame(frameId.current);
    };
  }, [minWidth, maxWidth]); // add dependencies

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      transitionDuration={300}
      ModalProps={{ keepMounted: true }}
      sx={{
        [`& .${drawerClasses.paper}`]: {
          width: isMobile ? '100vw' : panelWidth,
          minWidth: isMobile ? '100vw' : minWidth,
          maxWidth: isMobile ? '100vw' : maxWidth,
          backgroundColor: 'background.paper',
          borderLeft: isMobile ? 'none' : '1px solid #ddd',
          boxShadow: 10,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {!isMobile && (
        <Box
          onMouseDown={() => (isResizing.current = true)}
          sx={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: 8,
            cursor: 'ew-resize',
            zIndex: 999,
            '&:hover': {
              backgroundColor: 'rgba(0,0,0,0.1)',
            },
          }}
        />
      )}

      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid #eee',
          backgroundColor: 'background.paper',
          position: 'sticky',
          top: 0,
          zIndex: 1501,
        }}
      >
        <Typography
          variant="h6"
          fontWeight="bold"
          fontSize={isMobile ? '1rem' : '1.25rem'}
        >
          {title || t('Detail')}
        </Typography>

        <ButtonBase
          onClick={onClose}
          sx={{
            borderRadius: '50%',
            p: 1,
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
              transform: 'scale(1.1)',
            },
          }}
        >
          <CloseIcon fontSize="medium" />
        </ButtonBase>
      </Box>

      <Box
        sx={{
          flexGrow: 1,
          overflowY: 'auto', // <== Allow vertical scrolling
        }}
      >
        {children}
      </Box>
    </Drawer>
  );
}

SidePanel.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  children: PropTypes.node,
  title: PropTypes.any,
  closeOnOutsideClick: PropTypes.bool,
  width: PropTypes.number,
  minWidth: PropTypes.number,
  maxWidth: PropTypes.number,
};

export default SidePanel;